import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { notification, Spin } from 'antd';
import { FaArrowLeft } from 'react-icons/fa';
import { connect } from 'react-redux';
import { FormInput } from '../components/Basic/FormInput';
import Button from '../components/Basic/button';
import * as Actions from '../store/slices/smtpConfig/notifySmtpConfig';
import style from '../pages/notifyConfig/notifyconfig.module.css';
import formStyle from './Form.module.css';
import FormModal from '../components/Modal/FormModal';
import { hasPermission } from '../utils/premission';
import { PERMISSIONS } from '../utils/constant/permissions';

interface FormValues {
    domain: string;
    port: number;
    username: string;
    password: string;
}

const schema = yup.object({
    domain: yup.string().required('Domain is required'),
    port: yup.number().typeError('Port must be a number').required('Port is required').min(1).max(65535),
    username: yup.string().required('Username is required'),
    password: yup.string().required('Password is required'),
});

interface Props {
    Back: () => void;
    NotifySmtpConfig_Red: any;
    getNotifySmtpConfig: () => Promise<any>;
    upsertNotifySmtpConfig: (payload: any) => Promise<any>;
    deleteNotifySmtpConfig: () => Promise<any>;
}

const SmtpConfigForm: React.FC<Props> = ({ Back, NotifySmtpConfig_Red, getNotifySmtpConfig, upsertNotifySmtpConfig, deleteNotifySmtpConfig }) => {
    const [api, contextHolder] = notification.useNotification();
    const [spinning, setSpinning] = useState(false);
    const [configId, setConfigId] = useState<number>(0);
    const [deleteOpen, setDeleteOpen] = useState(false);

    const { control, handleSubmit, reset, formState: { errors } } = useForm<FormValues>({
        resolver: yupResolver(schema),
        defaultValues: { domain: '', port: 587, username: '', password: '' },
    });

    useEffect(() => {
        const load = async () => {
            setSpinning(true);
            const res = await getNotifySmtpConfig();
            if (res?.success && res.data) {
                const d = res.data;
                setConfigId(d.id || 0);
                reset({ domain: d.domain || '', port: d.port || 587, username: d.username || '', password: d.password || '' });
            }
            setSpinning(false);
        };
        load();
    }, []);

    const onSubmit = handleSubmit(async (values) => {
        setSpinning(true);
        const res = await upsertNotifySmtpConfig({ id: configId, ...values });
        if (res?.success) {
            setConfigId(res.data?.id || configId);
            api.success({ message: 'Configuration saved successfully' });
        } else {
            api.error({ message: 'Failed to save', description: res?.message });
        }
        setSpinning(false);
    });

    const onDelete = async () => {
        setDeleteOpen(false);
        setSpinning(true);
        const res = await deleteNotifySmtpConfig();
        if (res?.success) {
            setConfigId(0);
            reset({ domain: '', port: 587, username: '', password: '' });
            api.success({ message: 'Configuration deleted successfully' });
        } else {
            api.error({ message: 'Failed to delete', description: res?.message });
        }
        setSpinning(false);
    };

    return (
        <>
            {contextHolder}
            <Spin spinning={spinning} fullscreen />
            <FormModal
                modalTitle="Delete Configuration"
                Subtitle="Are you sure you want to delete this SMTP configuration?"
                open={deleteOpen}
                setOpen={setDeleteOpen}
                onOk={onDelete}
                onCancel={() => setDeleteOpen(false)}
            />

            <div className={style.Route}>
                <h5>
                    {/* <FaArrowLeft onClick={Back} style={{ cursor: 'pointer', marginRight: 8 }} /> */}
                    Configuration / SMTP Config
                </h5>
                <div className={style.btns}>
                    {configId > 0 && hasPermission(PERMISSIONS.NOTIFICATION_CONFIG_DELETE) && (
                        <Button Text="Delete" buttonClass={style.deleteBtn} onClick={() => setDeleteOpen(true)} Disable={spinning} />
                    )}
                    {hasPermission(PERMISSIONS.NOTIFICATION_CONFIG_UPSERT) && (
                        <Button Text="Save" buttonClass={style.createBtn} onClick={onSubmit} Disable={spinning} />
                    )}
                </div>
            </div>

            <div className={style.container}>
                <form onSubmit={onSubmit}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24 }}>

                        <div className={style.rowform}>
                            <div className={formStyle.field} style={{ minWidth: 280 }}>
                                <FormInput Label="Domain" placeholder="e.g. smtp.gmail.com" name="domain"
                                    control={control} errors={errors} classInput={formStyle.Input}
                                    className={formStyle.smtp_inputContainer} labelClass={formStyle.Label} classError={formStyle.Error} />
                            </div>
                            <div className={formStyle.field} style={{ minWidth: 160 }}>
                                <FormInput Label="Port" placeholder="587" name="port" type="number"
                                    control={control} errors={errors} classInput={formStyle.Input}
                                    className={formStyle.smtp_inputContainer} labelClass={formStyle.Label} classError={formStyle.Error} />
                            </div>
                        </div>

                        <div className={style.rowform}>
                            <div className={formStyle.field} style={{ minWidth: 280 }}>
                                <FormInput Label="Username" placeholder="SMTP username" name="username"
                                    control={control} errors={errors} classInput={formStyle.Input}
                                    className={formStyle.smtp_inputContainer} labelClass={formStyle.Label} classError={formStyle.Error} />
                            </div>
                            <div className={formStyle.field} style={{ minWidth: 280 }}>
                                <FormInput Label="Password" placeholder="SMTP password" name="password" type="password"
                                    control={control} errors={errors} classInput={formStyle.Input}
                                    className={formStyle.smtp_inputContainer} labelClass={formStyle.Label} classError={formStyle.Error} />
                            </div>
                        </div>

                    </div>
                </form>
            </div>
        </>
    );
};

function mapStateToProps(state: any) {
    return { NotifySmtpConfig_Red: state.NotifySmtpConfig_Red };
}

export default connect(mapStateToProps, Actions)(SmtpConfigForm);
